import React from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Paper,
  Avatar,
  Button,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  ArrowForward as ArrowIcon,
  Description as PRIcon,
  People as PeopleIcon,
  ViewKanban as TrelloIcon,
  Speed as EfficiencyIcon,
  ShoppingCart as TrollyIcon,
  Build as MaintenanceIcon,
  VerifiedUser as QualityIcon,
  Settings as SettingsIcon,
  Sensors as SensorsIcon,
  Bolt as EnergyIcon,
  DeleteSweep as WasteIcon,
  Replay as ReprocessingIcon,
  Opacity as DyeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { getAvatarUrl } from '../services/api';

const SystemSelection: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const systems = [
    {
      id: 'spare-parts',
      title: 'Spare Parts Management System',
      description: 'Manage inventory, transactions, QR codes, and maintenance for spare parts',
      icon: <InventoryIcon sx={{ fontSize: 60 }} />,
      color: '#00695C',
      path: '/spare-parts-system',
      roles: ['admin', 'manager', 'technician', 'viewer'],
    },
    {
      id: 'pr-management',
      title: 'PR Management System',
      description: 'Manage purchase requisitions, quotations, purchase orders, and GRN tracking',
      icon: <PRIcon sx={{ fontSize: 60 }} />,
      color: '#00695C',
      path: '/pr-management-system',
      roles: ['admin', 'manager', 'technician', 'viewer'],
    },
    {
      id: 'printing-process',
      title: 'Printing Process Management System',
      description: 'Manage printing workflows and tasks with Trello board integration',
      icon: <TrelloIcon sx={{ fontSize: 60 }} />,
      color: '#00695C',
      path: '/printing-process-system',
      roles: ['admin'], // Only admins can access
    },
    {
      id: 'machine-efficiency',
      title: 'Machine Efficiency Management System',
      description: 'Track and monitor machine efficiency, jobs, and time management for all machines',
      icon: <EfficiencyIcon sx={{ fontSize: 60 }} />,
      color: '#00695C',
      path: '/machine-efficiency-system',
      roles: ['admin', 'manager', 'technician', 'machine'], // Machine operators can access
    },
    {
      id: 'pfp-trolly',
      title: 'PFP Trolly Management System',
      description: 'Manage fabric LBS trolly inventory, stock additions, and release tracking',
      icon: <TrollyIcon sx={{ fontSize: 60 }} />,
      color: '#00695C',
      path: '/pfp-trolly-system',
      roles: ['admin', 'manager', 'technician'],
    },
    {
      id: 'maintenance-management',
      title: 'Maintenance Management System',
      description: 'Log and track machine failures, downtime, repair times, spare parts, root causes and corrective actions',
      icon: <MaintenanceIcon sx={{ fontSize: 60 }} />,
      color: '#00695C',
      path: '/maintenance-management-system',
      roles: ['admin', 'manager', 'technician', 'viewer'],
    },
    {
      id: 'quality-management',
      title: 'Quality Management System',
      description: 'Manage Strike Off & Bulk rejection reviews, multi-person approval workflows, and Trello card routing for internal and external rejections',
      icon: <QualityIcon sx={{ fontSize: 60 }} />,
      color: '#00695C',
      path: '/quality-management-system',
      roles: ['admin', 'manager', 'technician'],
    },
    {
      id: 'iot-system',
      title: 'Wireless Sensor Network Management',
      description: 'Monitor and manage IoT devices, track fabric width measurements, analyze defects in real-time',
      icon: <SensorsIcon sx={{ fontSize: 60 }} />,
      color: '#00695C',
      path: '/iot-system',
      roles: ['admin'],
    },
    {
      id: 'energy-management',
      title: 'Energy Management System',
      description: 'Monitor and analyze power, steam, and water consumption across machines, sections, and the department',
      icon: <EnergyIcon sx={{ fontSize: 60 }} />,
      color: '#00695C',
      path: '/energy-management-system',
      roles: ['admin', 'manager'],
    },
    {
      id: 'reprocessing-management',
      title: 'Reprocessing Management System',
      description: 'Track rejected batches through verification, color response, reprocessing, and finishing handover',
      icon: <ReprocessingIcon sx={{ fontSize: 60 }} />,
      color: '#00695C',
      path: '/reprocessing-management-system',
      roles: ['admin', 'manager', 'technician', 'viewer'],
    },
    {
      id: 'dye-stock-management',
      title: 'Dye Stock Management System',
      description: 'Manage warehouse and printing department digital dye stocks in barrels and kilograms, transfers, releases, and returns',
      icon: <DyeIcon sx={{ fontSize: 60 }} />,
      color: '#00695C',
      path: '/dye-stock-management-system',
      roles: ['admin', 'manager', 'technician', 'viewer'],
    },
    {
      id: 'waste-management',
      title: 'Waste Management System',
      description: 'Track waste by Trello card flow from Color Check through machine lists with PFP In and After Print weight controls',
      icon: <WasteIcon sx={{ fontSize: 60 }} />,
      color: '#00695C',
      path: '/waste-management-system',
      roles: ['admin', 'manager', 'technician', 'viewer'],
    },
    // Future systems can be added here:
    // {
    //   id: 'production',
    //   title: 'Production Management',
    //   description: 'Track production orders, schedules, and quality control',
    //   icon: <FactoryIcon sx={{ fontSize: 60 }} />,
    //   color: '#1976d2',
    //   path: '/production-system',
    // },
  ];

  const SUPER_ADMINS = ['admin', 'SasiriOCL'];

  // Filter systems based on allowed_systems and role.
  const filteredSystems = systems.filter(system => {
    // Admin role users and super admins always see all systems.
    if (user?.role === 'admin' || (user && SUPER_ADMINS.includes(user.username))) {
      return true;
    }

    // Non-admin users: explicit allowed_systems grants visibility.
    if (user?.allowed_systems && user.allowed_systems.length > 0) {
      return user.allowed_systems.includes(system.id);
    }

    // No explicit restriction defined — fall back to role-based visibility.
    return system.roles.includes(user?.role || '');
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: 'url(/ocean-lanka-bg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 0,
        },
      }}
    >
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          position: 'relative',
          zIndex: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 0,
          py: { xs: 1.5, sm: 2 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              <span style={{ color: '#00695C' }}>O</span>
              <span style={{ color: '#00A9E0' }}>C</span>
              <span style={{ color: '#00695C' }}>E</span>
              <span style={{ color: '#E31E24' }}>A</span>
              <span style={{ color: '#00695C' }}>N </span>
              <span style={{ color: '#00695C' }}>L</span>
              <span style={{ color: '#E31E24' }}>A</span>
              <span style={{ color: '#00695C' }}>N</span>
              <span style={{ color: '#00A9E0' }}>K</span>
              <span style={{ color: '#00695C' }}>A</span>
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: "'Brush Script MT', 'Segoe Script', cursive",
                fontStyle: 'italic',
                color: '#00695C',
                fontSize: { xs: '0.875rem', sm: '1rem' },
              }}
            >
              Printing Department
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }} flexWrap="wrap">
            <Box display="flex" alignItems="center" gap={1}>
              <Avatar
                src={getAvatarUrl(user?.profile_picture)}
                sx={{ bgcolor: '#00695C', width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 } }}
              >
                {!user?.profile_picture && <AccountIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />}
              </Avatar>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {user?.full_name || user?.username}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.role.toUpperCase()}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SettingsIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
              onClick={() => navigate('/settings')}
              sx={{
                borderColor: '#00695C',
                color: '#00695C',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1, sm: 2 },
                '&:hover': { borderColor: '#004d40', backgroundColor: 'rgba(0, 105, 92, 0.1)' },
              }}
            >
              Settings
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<LogoutIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
              onClick={handleLogout}
              sx={{
                borderColor: '#00695C',
                color: '#00695C',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1, sm: 2 },
                '&:hover': {
                  borderColor: '#004d40',
                  backgroundColor: 'rgba(0, 105, 92, 0.1)',
                },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Logout</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Out</Box>
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 4, sm: 6, md: 8 }, px: { xs: 2, sm: 3 } }}>
        <Box textAlign="center" mb={{ xs: 3, sm: 4, md: 6 }}>
          <Typography
            variant="h3"
            sx={{
              color: '#ffffff',
              fontWeight: 700,
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
              mb: { xs: 1, sm: 2 },
              fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            Select a System
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#ffffff',
              fontWeight: 300,
              textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
              px: { xs: 2, sm: 0 },
            }}
          >
            Choose which management system you want to access
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} justifyContent="center">
          {filteredSystems.map((system) => (
            <Grid item xs={12} sm={6} md={4} key={system.id}>
              <Card
                elevation={6}
                sx={{
                  height: '100%',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: { xs: 'scale(1.02)', sm: 'translateY(-8px)' },
                    boxShadow: `0 12px 24px rgba(0, 0, 0, 0.3)`,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => navigate(system.path)}
                  sx={{ height: '100%', p: { xs: 2, sm: 3 } }}
                >
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: '50%',
                        backgroundColor: `${system.color}20`,
                        mb: { xs: 2, sm: 3 },
                      }}
                    >
                      <Box sx={{ color: system.color, fontSize: { xs: 48, sm: 60 } }}>{system.icon}</Box>
                    </Box>
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ 
                        fontWeight: 600, 
                        color: system.color,
                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      }}
                    >
                      {system.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      paragraph
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {system.description}
                    </Typography>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={1}
                      sx={{ color: system.color, mt: { xs: 1, sm: 2 } }}
                    >
                      <Typography variant="button" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        Access System
                      </Typography>
                      <ArrowIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Future Systems Notice */}
        <Box
          sx={{
            mt: { xs: 3, sm: 4, md: 6 },
            p: { xs: 2, sm: 3 },
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            More management systems coming soon...
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default SystemSelection;
